import { useEffect, useMemo } from 'react';
import { useWizard } from 'react-use-wizard';

import { FundingPolicyType } from '@shared/appModel';
import { formatCurrency } from '@shared/formatCurrency';

import { ArrowLeft } from 'lucide-react';

import { Alert, AlertDescription, Button } from '@design-system';

import {
  useAddPolicyFromQuote,
  usePolicyTotalCost,
  getPolicyTypeLabel,
  useFundingSources,
} from '@/features/guided-financial-planning/hooks';
import { getStepIndex } from '@/features/guided-financial-planning/config';
import {
  useSession,
  useSessionMutations,
  useIsSessionComplete,
} from '@/features/guided-financial-planning/context';
import {
  useAllAllocations,
  useIsFullyFunded,
  usePolicyCost,
  getFundingAllocationActions,
} from '@/features/guided-financial-planning/stores';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { SelectedPolicyCard } from '@/features/guided-financial-planning/components/selected-policy-card';
import { FundingSourcesList } from '@/features/guided-financial-planning/components/funding-sources-list';

export function QuotesFundingStep() {
  const { nextStep, previousStep, goToStep } = useWizard();
  const { session } = useSession();
  const { updateStepData, isSaving } = useSessionMutations();
  const isSessionComplete = useIsSessionComplete();

  // Get self-funding sources to calculate total available assets (snapshot for completed sessions)
  const fundingSources = useFundingSources();
  const selfFunding = fundingSources?.selfFunding ?? {};

  const totalAvailableAssets = useMemo(() => {
    return Object.values(selfFunding).reduce((sum, source) => {
      const value = (source as SelfFundingSource).existingAssetsValue;
      return sum + (value ?? 0);
    }, 0);
  }, [selfFunding]);

  // Get the first selected quote from the hook
  const { selectedQuote: selectedQuoteInfo } = useAddPolicyFromQuote();
  const quoteId = selectedQuoteInfo?.quoteId ?? null;
  const selectedQuote = selectedQuoteInfo?.quote ?? null;

  // Calculate total policy cost using calcClientDerivatives
  const calculatedPolicyCost = usePolicyTotalCost();

  // Store selectors
  const allAllocations = useAllAllocations();
  const isFullyFunded = useIsFullyFunded();
  const policyCost = usePolicyCost();

  // Initialize store with active quote context
  useEffect(() => {
    getFundingAllocationActions().setActiveQuote(quoteId, calculatedPolicyCost);
  }, [quoteId, calculatedPolicyCost]);

  // Sync disabled state to store
  useEffect(() => {
    getFundingAllocationActions().setDisabled(isSessionComplete);
  }, [isSessionComplete]);

  // Sync store from session on mount/session change
  useEffect(() => {
    const sessionAllocations = session?.stepData?.policyFundingAllocations;
    if (sessionAllocations) {
      getFundingAllocationActions().initFromSession(
        sessionAllocations as Record<string, Record<string, number>>,
      );
    }
  }, [session?.stepData?.policyFundingAllocations]);

  const handleEditAccountDetails = () => {
    goToStep(getStepIndex('assessment-accounts'));
  };

  const handleBack = async () => {
    if (quoteId) {
      await updateStepData({ policyFundingAllocations: allAllocations });
      getFundingAllocationActions().markClean();
    }
    previousStep();
  };

  const handleNext = async () => {
    if (quoteId) {
      await updateStepData({ policyFundingAllocations: allAllocations });
      getFundingAllocationActions().markClean();
    }
    nextStep();
  };

  const policyTypeLabel = selectedQuote
    ? getPolicyTypeLabel(selectedQuote.fundingPolicyType as FundingPolicyType)
    : 'the policy';

  if (!selectedQuote || !quoteId) {
    return (
      <StepLayout
        title="How will you fund this policy?"
        subtitle={`Select which accounts you'll use to pay for the ${policyTypeLabel}. You can split the cost across multiple accounts.`}
        onBack={handleBack}
      >
        <div className="mx-auto max-w-2xl">
          <p className="text-center text-gray-500">No policy selected.</p>
        </div>
      </StepLayout>
    );
  }

  const hasInsufficientFunds = totalAvailableAssets < policyCost;

  return (
    <StepLayout
      title="How will you fund this policy?"
      subtitle={`Select which accounts you'll use to pay for the ${policyTypeLabel}. You can split the cost across multiple accounts.`}
      onBack={handleBack}
      onNext={handleNext}
      isNextDisabled={!isFullyFunded}
      nextButtonLabel="Calculate Impact"
      isLoading={isSaving}
    >
      {hasInsufficientFunds && (
        <div className="mx-auto mb-4 max-w-2xl">
          <Alert variant="warning">
            <AlertDescription className="flex flex-col items-center gap-3">
              <span>
                Your available assets ({formatCurrency(totalAvailableAssets)}) are not enough to
                cover the policy cost ({formatCurrency(policyCost)}). Consider selecting a different
                policy or updating your account balances.
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditAccountDetails}
              >
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                Edit Account Details
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}
      <div className="mx-auto max-w-2xl space-y-6">
        <SelectedPolicyCard />
        <FundingSourcesList />
      </div>
    </StepLayout>
  );
}
